import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import ClientPropertiesPanel from "../components/ClientPropertiesPanel";
import ClientPropertyDialog from "../components/ClientPropertyDialog";
import PropertyHistory from "../components/PropertyHistory";
import PropertyMap from "../components/properties/PropertyMap";
import { moveProperty, bulkMoveProperties } from "../components/MovePropertyDialog";
import { MagnifyingGlass, Buildings, TreeStructure, HouseLine, CaretRight, ArrowSquareOut, ClockCounterClockwise, X, Plus, ListBullets, MapTrifold } from "@phosphor-icons/react";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const Count = ({ n, label }) => <span className="text-[11px] text-muted-foreground">{label === "propert" ? plural(n, "property", "properties") : plural(n, label, `${label}s`)}</span>;
const PID = "text/property-id";

/** Clients > Sub-clients > Properties drill-down, a client/sub-client picker, a tenant-wide address lookup, quick add, drag-to-move and a map view. */
export default function PropertiesHubPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [adding, setAdding] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dropOver, setDropOver] = useState("");
  const [panelKey, setPanelKey] = useState(0);
  const selectedId = params.get("client") || "";
  const view = params.get("view") === "map" ? "map" : "list";

  const load = useCallback(() => API.get("/properties/overview").then((r) => setData(r.data)).catch(() => setData({ clients: [], totals: {} })), []);
  useEffect(() => { load(); }, [load]);

  // debounced address lookup
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); return undefined; }
    setSearching(true);
    const t = setTimeout(() => {
      API.get("/properties/search", { params: { q: term } }).then((r) => setResults(r.data.properties || [])).catch(() => setResults([])).finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const index = useMemo(() => {
    const m = {};
    (data?.clients || []).forEach((c) => { m[c.id] = { ...c, mainId: c.id }; c.sub_clients.forEach((s) => { m[s.id] = { ...s, mainId: c.id, mainName: c.name }; }); });
    return m;
  }, [data]);
  const selected = index[selectedId];
  const main = selected ? index[selected.mainId] : null;
  const accounts = useMemo(() => (data?.clients || []).flatMap((c) => c.sub_clients.length
    ? [{ id: c.id, label: `${c.name} — main account`, group: c.name }, ...c.sub_clients.map((s) => ({ id: s.id, label: `↳ ${s.name}`, group: c.name }))]
    : [{ id: c.id, label: c.name, group: "" }]), [data]);
  const moveTargets = main ? [{ id: main.id, label: `${main.name} — main account` }, ...main.sub_clients.map((s) => ({ id: s.id, label: `↳ ${s.name}` }))] : [];

  const setParam = (changes, replace = true) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    setParams(next, { replace });
  };
  const select = (id, keepQuery = false) => { if (!keepQuery) setQ(""); setParam({ client: id, ...(keepQuery ? {} : { q: "" }) }); };
  const onSearch = (v) => { setQ(v); setParam({ q: v.trim() ? v : "" }); };
  const refresh = () => { load(); setPanelKey((k) => k + 1); };

  const dropOn = async (e, targetId) => {
    e.preventDefault();
    setDropOver(""); setDragging(false);
    const pid = e.dataTransfer.getData(PID);
    if (!pid) return;
    const ids = (e.dataTransfer.getData("text/property-ids") || pid).split(",").filter(Boolean);
    try { if (ids.length > 1) await bulkMoveProperties(ids, targetId); else await moveProperty(pid, targetId); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not move property"); }
  };
  const dropProps = (id) => dragging ? {
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropOver !== id) setDropOver(id); },
    onDragLeave: () => setDropOver((d) => (d === id ? "" : d)),
    onDrop: (e) => dropOn(e, id),
  } : {};
  const dropCls = (id) => dragging ? (dropOver === id ? " ring-2 ring-primary bg-primary/15" : " ring-1 ring-dashed ring-primary/40") : "";

  const totals = data?.totals || {};
  const inSearch = q.trim().length >= 2;

  return (
    <div className="space-y-5" data-testid="properties-hub-page" onDragStart={(e) => { if (e.dataTransfer.types.includes(PID)) setDragging(true); }} onDragEnd={() => { setDragging(false); setDropOver(""); }}>
      <div className="flex flex-col lg:flex-row lg:items-end gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight" data-testid="page-title">Properties</h1>
          <p className="text-sm text-muted-foreground mt-1">Every registered property across your clients — drill down from the main client to its sub-clients, or jump straight to an address.
            {data && <span className="ml-1" data-testid="properties-totals">{plural(totals.properties || 0, "property", "properties")} · {plural(totals.clients || 0, "client", "clients")} · {plural(totals.sub_clients || 0, "sub-client", "sub-clients")}</span>}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 lg:w-[760px]">
          <select value={selectedId} onChange={(e) => select(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm sm:w-[280px]" data-testid="properties-client-select">
            <option value="">Choose a client or sub-client…</option>
            {(data?.clients || []).map((c) => c.sub_clients.length ? (
              <optgroup key={c.id} label={`${c.name} (${c.family_property_count})`}>
                <option value={c.id}>{c.name} — main account ({c.property_count})</option>
                {c.sub_clients.map((s) => <option key={s.id} value={s.id}>↳ {s.name} ({s.property_count})</option>)}
              </optgroup>
            ) : <option key={c.id} value={c.id}>{c.name} ({c.property_count})</option>)}
          </select>
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => onSearch(e.target.value)} placeholder="Search by address, postcode or property name…" className="pl-9 pr-8 h-10" data-testid="properties-search-input" />
            {q && <button onClick={() => onSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="properties-search-clear"><X size={14} /></button>}
          </div>
          <Button className="h-10" onClick={() => setAdding(true)} disabled={!data} data-testid="hub-add-property-btn"><Plus size={14} className="mr-1" /> Add property</Button>
        </div>
      </div>

      <ClientPropertyDialog clientId={selectedId} accounts={accounts} open={adding} onClose={() => setAdding(false)}
        onSaved={(_p, accountId) => { setQ(""); setParam({ client: accountId || selectedId, view: "", q: "" }); refresh(); }} />

      {inSearch ? (
        <SearchResults results={results} searching={searching} q={q} onShow={(p) => select(p.client_id)} historyFor={historyFor} setHistoryFor={setHistoryFor} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[300px_300px_minmax(0,1fr)]" data-testid="properties-drilldown">
          <Column title="Clients" icon={Buildings} count={data?.clients.length} testid="hub-clients-column">
            {data === null ? <p className="text-xs text-muted-foreground p-3">Loading…</p> : data.clients.length === 0 ? <p className="text-xs text-muted-foreground p-3">No clients yet.</p> : data.clients.map((c) => (
              <button key={c.id} onClick={() => select(c.id)} data-testid={`hub-client-${c.id}`} {...(main?.id === c.id && dragging ? dropProps(c.id) : {})}
                className={`w-full text-left px-3 py-2.5 border-b border-border/60 last:border-0 hover:bg-muted/50 flex items-center gap-3 ${main?.id === c.id ? "bg-primary/10 border-l-2 border-l-primary" : ""}${main?.id === c.id ? dropCls(c.id) : ""}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{c.name}</p>
                  <div className="flex gap-2 flex-wrap"><Count n={c.family_property_count} label="propert" />{c.sub_clients.length > 0 && <Count n={c.sub_clients.length} label="sub-client" />}{c.town && <span className="text-[11px] text-muted-foreground">{c.town}</span>}</div>
                </div>
                <CaretRight size={14} className="text-muted-foreground shrink-0" />
              </button>
            ))}
          </Column>

          <Column title="Sub-clients" icon={TreeStructure} count={main ? main.sub_clients.length : undefined} testid="hub-subclients-column">
            {!main ? <p className="text-xs text-muted-foreground p-3">Pick a client to see its sub-clients and its own properties.</p> : (
              <>
                <button onClick={() => select(main.id)} data-testid="hub-main-own" {...dropProps(main.id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-border/60 hover:bg-muted/50 flex items-center gap-3 ${selectedId === main.id ? "bg-primary/10 border-l-2 border-l-primary" : ""}${dropCls(main.id)}`}>
                  <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{main.name} <span className="text-[11px] text-muted-foreground font-normal">· main account</span></p><Count n={main.property_count} label="propert" /></div>
                  <CaretRight size={14} className="text-muted-foreground shrink-0" />
                </button>
                {main.sub_clients.length === 0 && <p className="text-xs text-muted-foreground p-3">No sub-clients under {main.name}. <Link to={`/clients/${main.id}`} className="text-primary hover:underline">Add one on the client record</Link>.</p>}
                {main.sub_clients.map((s) => (
                  <button key={s.id} onClick={() => select(s.id)} data-testid={`hub-sub-${s.id}`} {...dropProps(s.id)}
                    className={`w-full text-left px-3 py-2.5 border-b border-border/60 last:border-0 hover:bg-muted/50 flex items-center gap-3 ${selectedId === s.id ? "bg-primary/10 border-l-2 border-l-primary" : ""}${dropCls(s.id)}`}>
                    <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">↳ {s.name}</p><div className="flex gap-2"><Count n={s.property_count} label="propert" />{s.town && <span className="text-[11px] text-muted-foreground">{s.town}</span>}</div></div>
                    <CaretRight size={14} className="text-muted-foreground shrink-0" />
                  </button>
                ))}
                {dragging && <p className="text-[11px] text-primary p-2 text-center" data-testid="hub-drop-hint">Drop the property on its new account</p>}
              </>
            )}
          </Column>

          <div className="min-w-0" data-testid="hub-properties-column">
            <nav className="flex items-center gap-1.5 text-sm mb-3 flex-wrap" data-testid="hub-breadcrumb">
              {selected ? (
                <>
                  <button onClick={() => select(main.id)} className="text-primary hover:underline">{main.name}</button>
                  {selected.id !== main.id && <><CaretRight size={12} className="text-muted-foreground" /><span className="font-medium">{selected.name}</span></>}
                  <CaretRight size={12} className="text-muted-foreground" /><span className="text-muted-foreground">Properties{view === "map" && selected.id === main.id && main.sub_clients.length > 0 ? " (incl. sub-clients)" : ""}</span>
                </>
              ) : <span className="text-muted-foreground">{view === "map" ? "Whole portfolio" : "Properties"}</span>}
              <div className="ml-auto flex items-center gap-1">
                {selected && <Button asChild variant="ghost" size="sm" className="h-7 text-xs"><Link to={`/clients/${selected.id}`} data-testid="hub-open-client-link"><ArrowSquareOut size={12} className="mr-1" /> Open {selected.id === main.id ? "client" : "sub-client"} record</Link></Button>}
                <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="hub-view-toggle">
                  <button onClick={() => setParam({ view: "" })} className={`px-2.5 h-7 text-xs inline-flex items-center gap-1 ${view === "list" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="hub-view-list"><ListBullets size={12} /> List</button>
                  <button onClick={() => setParam({ view: "map" })} className={`px-2.5 h-7 text-xs inline-flex items-center gap-1 ${view === "map" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="hub-view-map"><MapTrifold size={12} /> Map</button>
                </div>
              </div>
            </nav>
            {view === "map" ? (
              <PropertyMap clientId={selectedId} onShowInList={(p) => { setQ(""); setParam({ client: p.client_id, view: "", q: "" }); }} />
            ) : !selected ? (
              <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground flex flex-col items-center gap-2" data-testid="hub-properties-empty">
                <HouseLine size={28} className="text-muted-foreground/60" />
                Choose a client or sub-client on the left — or search an address above — to see its registered properties.
                <Button variant="outline" size="sm" className="mt-2 h-7 text-xs" onClick={() => setParam({ view: "map" })} data-testid="hub-empty-map-btn"><MapTrifold size={12} className="mr-1" /> See the whole portfolio on a map</Button>
              </div>
            ) : (
              <ClientPropertiesPanel key={selected.id} clientId={selected.id} refreshKey={panelKey} moveTargets={moveTargets.length > 1 ? moveTargets : undefined} onChanged={load} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Column({ title, icon: Icon, count, children, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden self-start" data-testid={testid}>
      <div className="px-3 py-2 border-b border-border bg-muted/30 flex items-center gap-2"><Icon size={14} className="text-primary" /><h2 className="text-xs font-semibold uppercase tracking-wider">{title}</h2>{count != null && <Badge variant="outline" className="ml-auto text-[10px] h-5">{count}</Badge>}</div>
      <div className="max-h-[70vh] overflow-y-auto">{children}</div>
    </div>
  );
}

function SearchResults({ results, searching, q, onShow, historyFor, setHistoryFor }) {
  return (
    <div className="rounded-lg border border-border bg-card" data-testid="properties-search-results">
      <div className="px-4 py-2.5 border-b border-border flex items-center gap-2 text-sm">
        <MagnifyingGlass size={14} className="text-primary" />
        <span className="font-medium">{searching && results === null ? "Searching…" : `${plural((results || []).length, "property", "properties")} matching “${q.trim()}”`}</span>
        <span className="text-xs text-muted-foreground ml-auto">Searches every client's address book — name, first line, town, county and postcode</span>
      </div>
      {results && results.length === 0 && !searching && <p className="text-sm text-muted-foreground p-6 text-center" data-testid="properties-search-empty">Nothing matches. Try a postcode or the first line of the address.</p>}
      <ul className="divide-y divide-border/60">
        {(results || []).map((p) => (
          <li key={p.id} className="px-4 py-3" data-testid={`search-result-${p.id}`}>
            <div className="flex items-start gap-3 flex-wrap">
              <HouseLine size={18} className="text-muted-foreground mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.address}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1 flex-wrap" data-testid={`search-result-path-${p.id}`}>
                  <Buildings size={11} /> {p.parent_name ? <>{p.parent_name} <CaretRight size={10} /> {p.client_name}</> : p.client_name}
                  {(p.job_count || p.request_count || p.emergency_count) ? <span>· {[p.request_count && `${p.request_count} requests`, p.job_count && `${p.job_count} jobs`, p.emergency_count && `${p.emergency_count} emergencies`].filter(Boolean).join(", ")}</span> : null}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onShow(p)} data-testid={`search-show-${p.id}`}><TreeStructure size={12} className="mr-1" /> Show in {p.parent_name ? "sub-client" : "client"}</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setHistoryFor(historyFor === p.id ? null : p.id)} data-testid={`search-history-${p.id}`}><ClockCounterClockwise size={12} className="mr-1" /> {historyFor === p.id ? "Hide history" : "History"}</Button>
                <Button asChild size="sm" variant="ghost" className="h-7 text-xs"><Link to={`/clients/${p.client_id}`} data-testid={`search-open-client-${p.id}`}><ArrowSquareOut size={12} className="mr-1" /> Client</Link></Button>
              </div>
            </div>
            {historyFor === p.id && <div className="mt-2 border-t border-border pt-2"><PropertyHistory endpoint={`/clients/${p.client_id}/properties/${p.id}/history`} testid={`search-property-history-${p.id}`} /></div>}
          </li>
        ))}
      </ul>
    </div>
  );
}
